// Import
import i18n from '@/i18n'
import Vue from 'vue'
import store from '@/store'
import { Api } from '@/core/api.js'

import { FORMAT, TOOLS } from '@/constants'
import { buildRadarModuleState } from '@/store/utils/radarKpis.js'
import { buildHierarchicalOrderedItems, getBaseOrder } from '@/store/utils/checklistItems'
import { postTaskWithRetry, shouldQueueOfflineSync } from '@/utils/taskApiRetry'
import { db_registers_queue, db_templates, db_registers, db_registers_schemas, db_issues } from '@/database'
import { buildItemCustomMapFromTemplates, mergeTemplateCustomIntoItem, shouldHideItem, getCustomApplyForRegister, getCustomApply, getTemplateItemById } from '@/utils/templateCustom'

const api = new Api()

// Triquiñuela para resetear el state
const getDefaultState = () => {
    return {
        stats: {
            alerts: {},
            activity: {}
        },
        records: {},
        totalRecords: 0,
        totalDraftRecords: 0,
        totalCompletedRecords: 0,
        number_page: 1,
        categories: {},
        current: false,
        templates: {},
        templatesLoaded: false, // needed by Radar
        headers: {},
        type: 3,
        config: {
            date_format: 'YYYY-MM-DD',
            hour_format: 'HH:mm',
            datetime_format: 'YYYY-MM-DD HH:mm',
            has_score: false,
            has_state: false,
            has_timing: false,
            has_mandatory: true,
            is_autosave: true,
            can_create: true,
            item_search: false,
            has_calendar: false,
            temporal_view: false,
            close_when_completed: false,
            auth_users: {
                pin: 2,
                review: 2,
                task: 1
            },
            can_edit: {
                title: false,
                state: false,
                inPast: true,
                inTime: true,
                inFuture: true
            },
            item: {
                itemsTitle: [FORMAT.TITLE, FORMAT.SUBTITLE],
                itemsImage: [FORMAT.PHOTO, FORMAT.SIGNATURE],
                itemsInfo: [FORMAT.DOCUMENT, FORMAT.MESSAGE],
                itemsExcluded: [FORMAT.TITLE, FORMAT.SUBTITLE, FORMAT.DOCUMENT, FORMAT.MESSAGE, FORMAT.LINK, FORMAT.RESOURCE],
                itemsVisibles: [FORMAT.TITLE, FORMAT.SUBTITLE, FORMAT.MESSAGE],
                can_pospone: false,
                rate: false, // Esto es por si van a tener puntuaciones en funcion de lo que se responda
                can_review: false,
                has_periodicity: false,
                has_score: false
            }
        },
        recordsAreLoaded: false,
        isLoading: false,
        previousEditId: null,
        tabPages: {
            draft: null,
            completed: null
        },
        radarKPIs: null
    }
}

// initial state
const state = getDefaultState()

const loadLocalForageInformation = async () => {
    const registersPage = await db_templates.getItem('registers_page')
    if (registersPage) state.number_page = registersPage

    const registersItems = await db_templates.getItem('registers_items')
    if (registersItems) state.totalRecords = registersItems

    const registersDraftItems = await db_templates.getItem('registers_draft_items')
    if (registersDraftItems) state.totalDraftRecords = registersDraftItems

    const registersCompletedItems = await db_templates.getItem('registers_completed_items')
    if (registersCompletedItems) state.totalCompletedRecords = registersCompletedItems
}

loadLocalForageInformation()

// getters
const getters = {
    getIsLoading: (state, getters, rootState) => {
        return state.isLoading
    },
    getRecordsAreLoaded: (state, getters, rootState) => {
        return state.recordsAreLoaded
    },
    getById: (state, getters, rootState) => (id) => {
        return id ? state.records[id] : false
    },
    getConfig: (state, getters, rootState) => {
        return { ...state.config }
    },
    getTemplates: (state, getters, rootState) => {
        return state.templates
    },
    getTemplatesLoaded: (state) => {
        return state.templatesLoaded
    },
    getTemplateById: (state, getters, rootState) => (id) => {
        return state.templates[id]
    },
    getSubtasks: (state, getters, rootState) => (recordId, task) => {
        let tasks = recordId ? state.records[recordId].items : {}
        let subtask = []

        Object.keys(tasks).map(function (item_id, i) {
            var item = tasks[item_id]
            if (item.parent_id == task) {
                subtask.push(item.item_id)
            }
        })

        return subtask
    },
    getAllStats: (state, getters, rootState) => {
        return state.stats
    },
    getStats: (state, getters, rootState) => (wich) => {
        return state.stats[wich]
    },
    getTotalRecords: (state, getters) => {
        return state.totalRecords
    },
    getTotalDraftRecords: (state, getters) => {
        return state.totalDraftRecords
    },
    getTotalCompletedRecords: (state, getters) => {
        return state.totalCompletedRecords
    },
    getNumberPage: (state, getters) => {
        return state.number_page
    },
    getRecords: (state, getters, rootState) => {
        return state.records
    },
    getDraftRecords: (state, getters) => {
        // filtrar los registros que tengan status 0
        return Object.values(state.records).filter((record) => record.status === 0)
    },
    getCompletedRecords: (state, getters) => {
        // filtrar los registros que tengan status 1
        return Object.values(state.records).filter((record) => record.status === 1)
    },
    getHeaders: (state, getters, rootState) => {
        return state.headers
    },
    getPreviousEditId: (state, getters, rootState) => {
        return state.previousEditId
    },
    getCategories: (state, getters, rootState) => (id) => {
        if (id) {
            return state.categories[id]
        }
        return state.categories
    },
    getTemplatesSearched: (state, getters, rootState) => (search) => {
        var searchValues = search.split(' ')
        var results = []

        for (var templatesIndex in state.templates) {
            var template = state.templates[templatesIndex]
            var templateName =
                template.name != null
                    ? template.name
                          .trim()
                          .normalize('NFD')
                          .replace(/[\u0300-\u036f]/g, '')
                          .toLowerCase()
                    : ''

            template.searchscore = 0

            for (var searchIndex in searchValues) {
                var searchWord = searchValues[searchIndex]
                    .normalize('NFD')
                    .replace(/[\u0300-\u036f]/g, '')
                    .toLowerCase()

                template.searchscore = templateName.indexOf(searchWord) > -1 ? (template.searchscore += 1 + searchWord.length * 0.1) : template.searchscore
            }

            if (template.searchscore > 0) {
                results.push(template)
            }
        }

        results.sort((a, b) => (parseInt(a.searchscore) < parseInt(b.searchscore) ? 1 : -1))

        return results
    },
    getItemsBySection: (state, getters, rootState) => (recordId, section) => {
        var newSectionFound = false
        var startLooking = false
        var resultItems = {}
        var items = recordId ? Object.values({ ...state.records[recordId].items }) : []

        // CLEAN OF SUBITEMS
        items = items.filter((obj) => {
            return !obj.parent_id
        })

        // FIRST SORT BY ORDER AND ADD ITEMS TO RESULT
        items
            .sort(function (l, r) {
                return l.order - r.order
            })
            .forEach(function (item) {
                if (item.type != 8 && startLooking && !newSectionFound) {
                    resultItems[item.item_id] = item
                } else {
                    if (!startLooking && item.item_id == section) {
                        startLooking = true
                    } else if (startLooking) {
                        newSectionFound = true
                        startLooking = false
                    }
                }
            })

        return resultItems
    },
    getItemsWithTemporalId(state, getters, rootState) {
        return Object.values(state.records).filter((item) => item.temporal_id)
    },
    getNotesById: (state) => (id) => {
        if (state.records[id] && state.records[id].notes) {
            return state.records[id].notes
        }
        return state.records[id].notes
    },
    getTabPages: (state, getters, rootState) => {
        return state.tabPages
    },
    getRadarModuleState: (state, getters, rootState, rootGetters) => (kpiKeys) => {
        const result = buildRadarModuleState(TOOLS.REGISTERS, state.radarKPIs, kpiKeys, {}, rootGetters)
        return result
    }
}

// actions
const actions = {
    resetState({ commit }) {
        commit('resetState')
    },
    async addIssue(context, params) {
        context.dispatch('calcScore', params.checklist_ref)

        const checklist = context.getters.getById(params.checklist_ref)
        const na = Object.values(checklist.items).filter((item) => item.apply === '0').length
        const resume = { it: 0, is: 0, a: 0, n: 0, na: na }

        for (let id in checklist.items) {
            const item = checklist.items[id]

            if (!shouldHideItem(item) && item.is_visible && !state.config.item.itemsExcluded.includes(item.type)) {
                if (!item.complete_date && item.apply) resume.it++ // if item does not have completed_date
                if (item.message) resume.n++ // if item has notes
                if (item.alerts && item.alerts.issues.length > 0) resume.is++ // if item has alerts and has issue
                if (item.alerts && item.alerts.issues.length == 0) resume.a++ // if item has alerts and does not have issue
                if (!item.apply) resume.na++
            }
        }

        var oData = { template: params.tpl_id }
        if (params.checklist_ref) {
            oData.checklist_ref = params.checklist_ref
        }
        if (params.item_ref) {
            oData.item_ref = params.item_ref
        }

        oData.resume = JSON.stringify(resume)

        if (params.isOfflineCreated) {
            oData.isOfflineCreated = true
        }

        if (params.toolQueue) {
            oData.toolQueue = params.toolQueue
        }

        const canWorkOffline = context.rootGetters.getOffline

        if (fnCheckConnection() && context.rootGetters.getFastConnection) {
            return api.post('checklist/add', oData).then(function (response) {
                var items = { ...state.records }
                var reference = {
                    checklist: Object.values(response.data)[0].id,
                    type: Object.values(response.data)[0].type,
                    status: Object.values(response.data)[0].status
                }
                var item = {
                    ...items[params.checklist_ref].items[params.item_ref]
                }
                item.reference = reference
                items[params.checklist_ref].items[params.item_ref] = item
                Vue.set(state, 'items', items)
                return response.data
            })
        } else {
            if (canWorkOffline) {
                const issue = await store.dispatch('issues/add', oData)

                const reference = {
                    checklist: issue.id,
                    type: issue.type,
                    status: issue.status
                }

                context.state.records[params.checklist_ref].items[params.item_ref].reference = reference

                await db_issues.getItem(params.checklist_ref).then(async (register) => {
                    if (register) {
                        register.items[params.item_ref].reference = reference
                        await db_issues.setItem(params.checklist_ref, register)
                    }
                })
                return issue
            }
        }
    },
    async comment(context, params) {
        context.dispatch('calcScore', params.check)

        const checklist = context.getters.getById(params.check)
        const total = checklist.total
        const answered = checklist.completed
        const score = ((parseFloat(answered) * 100) / parseFloat(total)).toFixed(2)

        const na = Object.values(checklist.items).filter((item) => item.apply === '0').length
        const resume = { it: 0, is: 0, a: 0, n: 1, na: na }

        for (let id in checklist.items) {
            let item = checklist.items[id]

            if (!shouldHideItem(item) && item.is_visible && !state.config.item.itemsExcluded.includes(item.type)) {
                if (!item.complete_date && item.apply) resume.it++ // if item does not have completed_date
                if (item.message) resume.n++ // if item has notes
                if (item.alerts && item.alerts.issues.length > 0) resume.is++ // if item has alerts and has issue
                if (item.alerts && item.alerts.issues.length == 0) resume.a++ // if item has alerts and does not have issue
                if (!item.apply) resume.na++
            }
        }

        var sended = {
            action: 'comment',
            checklist: params.check,
            task: params.answer,
            message: params.message,
            total: total,
            answered: answered,
            score: score,
            resume: JSON.stringify(resume)
        }

        if (typeof params.category !== 'undefined') sended.category = params.category
        if (typeof params.file !== 'undefined') sended.file = params.file

        const canWorkOffline = context.rootGetters.getOffline

        if (fnCheckConnection() && context.rootGetters.getFastConnection) {
            const { response } = await postTaskWithRetry(api, 'checklist/task/comment', sended)

            if (!response.status && context.rootGetters.getOffline) {
                if (shouldQueueOfflineSync(response)) {
                    const offlineMessage = {
                        check: params.check,
                        answer: params.answer,
                        message: params.message,
                        requireSync: !(fnCheckConnection() && context.rootGetters.getFastConnection) && canWorkOffline
                    }
                    if (typeof params.file !== 'undefined') offlineMessage.image = params.file

                    return context
                        .dispatch('syncAction', sended)
                        .then(() => context.commit('setComment', offlineMessage))
                        .catch(() => context.commit('setComment', offlineMessage))
                }
                return context.dispatch('syncAction', sended)
            }

            if (!response.status) {
                var error = {
                    check: params.check,
                    answer: params.answer
                }
                if (typeof params.file !== 'undefined') error.image = null
                else error.message = ''

                context.commit('setComment', error)
                return { status: false }
            }

            var returned = {
                check: params.check,
                answer: params.answer,
                message: response.data.message
            }
            if (typeof response.data.image !== 'undefined') {
                returned.image = response.data.image
            }

            context.commit('setComment', returned)
            return { status: true }
        } else if (context.rootGetters.getOffline) {
            let offlineMessage = {
                check: params.check,
                answer: params.answer,
                message: params.message,
                requireSync: !(fnCheckConnection() && context.rootGetters.getFastConnection) && canWorkOffline
            }
            if (typeof params.file !== 'undefined') offlineMessage.image = params.file
            return context
                .dispatch('syncAction', sended)
                .then((response) => {
                    return context.commit('setComment', offlineMessage)
                })
                .catch(() => {
                    return context.commit('setComment', offlineMessage)
                })
        } else {
            var error = {
                check: params.check,
                answer: params.answer
            }
            if (typeof params.file !== 'undefined') error.image = null
            else error.message = ''

            context.commit('setComment', error)
            return { status: false }
        }
    },
    complete(context, params) {
        context.dispatch('calcScore', params.check)

        const checklist = context.getters.getById(params.check)
        const wasDraft = parseInt(checklist.status, 10) === 0
        const na = Object.values(checklist.items).filter((item) => item.apply === '0').length
        const resume = { it: 0, is: 0, a: 0, n: 0, na: na }

        for (let id in checklist.items) {
            const item = checklist.items[id]

            if (!shouldHideItem(item) && item.is_visible && !state.config.item.itemsExcluded.includes(item.type)) {
                if (!item.complete_date && item.apply) resume.it++ // if item does not have completed_date
                if (item.message) resume.n++ // if item has notes
                if (item.alerts && item.alerts.issues.length > 0) resume.is++ // if item has alerts and has issue
                if (item.alerts && item.alerts.issues.length == 0) resume.a++ // if item has alerts and does not have issue
                if (!item.apply) resume.na++
            }
        }

        const canWorkOffline = context.rootGetters.getOffline
        const paramsApi = {
            checklist: params.check,
            employee: params.emp,
            complete_date: params.date,
            resume: JSON.stringify(resume)
            // type: context.state.type,
        }
        if (fnCheckConnection() && context.rootGetters.getFastConnection) {
            return api.post('checklist/complete', paramsApi).then(function (response) {
                if (response.status && wasDraft) {
                    context.dispatch('adjustDraftRecordsCounters', {
                        templateId: checklist.template_id,
                        delta: -1
                    })
                }

                var data = Object.values(response.data)[0]
                // params.values = JSON.parse(params.values);
                // Object.keys(params.values).map(function (id, i) {
                Object.keys(data.items).map(function (id, i) {
                    var val = data.items[id]

                    if (state.config.item.itemsImage.includes(val.type)) {
                        val.value = val.value ? splitCommasBase64(val.value) : val.value
                    }

                    context.commit('setAnswer', {
                        check: data.id,
                        answer: val.item_id,
                        emp: val.employee.id,
                        date: data.complete_date,
                        value: val.value
                    })
                })
                return response
            })
        } else {
            if (canWorkOffline) {
                paramsApi.action = 'complete'
                paramsApi.status = 1

                return context
                    .dispatch('syncAction', paramsApi)
                    .then(() => {
                        Object.keys(checklist.items).map((id, i) => {
                            const val = checklist.items[id]

                            if (state.config.item.itemsImage.includes(val.type)) {
                                val.value = val.value && val.value.includes(',') ? splitCommasBase64(val.value) : val.value
                            }
                            const answerObj = {
                                check: params.check,
                                answer: val.item_id,
                                emp: val.employee ? val.employee.id : val.employee,
                                date: params.date,
                                value: val.value,
                                requireSync: !(fnCheckConnection() && context.rootGetters.getFastConnection) && canWorkOffline
                            }
                            context.commit('setAnswer', answerObj)
                        })

                        //Save check complete
                        checklist.complete_date = params.date
                        checklist.status = 1
                        const allItems = { ...context.state.records, [params.check]: checklist }
                        context.dispatch('format', { data: allItems })
                        context.dispatch('calcScore', params.check)

                        if (wasDraft) {
                            context.dispatch('adjustDraftRecordsCounters', {
                                templateId: checklist.template_id,
                                delta: -1
                            })
                        }

                        return { status: true }
                    })
                    .catch(() => {
                        Object.keys(checklist.items).map((id, i) => {
                            const val = checklist.items[id]

                            if (state.config.item.itemsImage.includes(val.type)) {
                                val.value = val.value && val.value.includes(',') ? splitCommasBase64(val.value) : val.value
                            }
                            const answerObj = {
                                check: params.check,
                                answer: val.item_id,
                                emp: val.employee ? val.employee.id : val.employee,
                                date: params.date,
                                value: val.value,
                                requireSync: !(fnCheckConnection() && context.rootGetters.getFastConnection) && canWorkOffline
                            }
                            context.commit('setAnswer', answerObj)
                        })

                        // Save check complete
                        checklist.complete_date = params.date
                        checklist.status = 1
                        const allItems = { ...context.state.records, [params.check]: checklist }
                        context.dispatch('format', { data: allItems })
                        context.dispatch('calcScore', params.check)

                        if (wasDraft) {
                            context.dispatch('adjustDraftRecordsCounters', {
                                templateId: checklist.template_id,
                                delta: -1
                            })
                        }

                        return { status: true }
                    })
            } else {
                return { status: false }
            }
        }
    },
    loadById(context, params) {
        return api.get('checklist/id/' + params.id, {}).then(function (response) {
            context.dispatch('format', { data: response.data })
            context.dispatch('calcScore', params.id)
        })
    },
    loadCategories(context) {
        if (fnCheckConnection() && context.rootGetters.getFastConnection) {
            return api.get('checklist/3/categories').then(function (response) {
                var categories = response.data
                if (Object.values(response.data).length > 0) {
                    categories['all'] = {
                        id: 'all',
                        name: i18n.t('registers.all_registers'),
                        highlight: true,
                        order: -1,
                        color: '#374673',
                        type: 3
                    }
                }

                context.commit('setCategories', categories)
            })
        }
    },
    loadTemplates(context, params) {
        if (!fnCheckConnection()) {
            return loadTemplatesOffline(context)
        }

        return api
            .get('checklist/templates/' + state.type, {})
            .then(function (response) {
                context.commit('setTemplates', { data: response.data })
                context.commit('setTemplatesLoaded', true)

                _.forEach(_.values(response.data), async function (value, key) {
                    await db_templates.setItem(value.id, JSON.stringify(value))
                })
            })
            .catch((error) => {
                logError(error)
                return loadTemplatesOffline(context)
            })
    },
    adjustDraftRecordsCounters(context, params) {
        const delta = parseInt(params.delta, 10)

        if (!delta) {
            return
        }

        const currentTotalDraftRecords = parseInt(context.state.totalDraftRecords, 10) || 0
        context.commit('setTotalDraftRecords', Math.max(0, currentTotalDraftRecords + delta))
        context.commit('adjustTemplateDraftRecordsCount', {
            templateId: params.templateId,
            delta: delta
        })
        context.commit('adjustRadarDraftRecordsKPI', {
            templateId: params.templateId,
            delta: delta
        })
    },
    reload(context, params) {
        context.dispatch('format', { data: checklist })
        context.dispatch('calcScore')
    },
    async answer(context, params) {
        let value = params.value
        let subtask = context.getters.getSubtasks(params.check, params.answer)
        const canWorkOffline = context.rootGetters.getOffline

        // In case of image or signature type, JSON-parse the value
        if (state.config.item.itemsImage.includes(params.answer_type)) {
            value = JSON.stringify(value)
        }

        // answer
        context.commit('setAnswer', {
            check: params.check,
            answer: params.answer,
            emp: params.emp,
            date: params.date,
            value: params.value,
            requireSync: !(fnCheckConnection() && context.rootGetters.getFastConnection) && canWorkOffline,
            bReadingFromSensor: params.bReadingFromSensor
        })

        // calculate score
        await context.dispatch('calcScore', params.check)

        if (subtask.length > 0) {
            Object.keys(subtask).map(function (item_id, i) {
                var item = subtask[item_id]
                context.commit('setReset', { check: params.check, answer: item })
            })
        }

        const checklist = context.getters.getById(params.check)
        const total = checklist.total
        const answered = checklist.completed
        const score = ((parseFloat(answered) * 100) / parseFloat(total)).toFixed(2)
        const na = Object.values(checklist.items).filter((item) => item.apply === '0').length
        const resume = { it: 0, is: 0, a: 0, n: 0, na: na }

        for (let id in checklist.items) {
            const item = checklist.items[id]
            if (!shouldHideItem(item) && item.is_visible && !state.config.item.itemsExcluded.includes(item.type)) {
                if (!item.complete_date && item.apply) resume.it++ // if item does not have completed_date
                if (item.message) resume.n++ // if item has notes
                if (item.alerts && item.alerts.issues.length > 0) resume.is++ // if item has alerts and has issue
                if (item.alerts && item.alerts.issues.length == 0) resume.a++ // if item has alerts and does not have issue
                if (!item.apply) resume.na++
            }
        }

        const paramsApi = {
            checklist: params.check,
            task: params.answer,
            employee: params.emp,
            complete_date: params.date,
            value: value,
            total: total,
            answered: answered,
            score: score,
            resume: JSON.stringify(resume),
            type: params.answer_type,
            bReadingFromSensor: params.bReadingFromSensor
        }

        if (canWorkOffline) {
            await context.dispatch('clearOfflineQueue', { data: paramsApi, action: 'answer' })
        }

        if (fnCheckConnection() && context.rootGetters.getFastConnection) {
            const { response } = await postTaskWithRetry(api, 'checklist/task/answer', paramsApi)

            if (!response.status) {
                if (canWorkOffline && shouldQueueOfflineSync(response)) {
                    paramsApi.action = 'answer'
                    try {
                        await context.dispatch('syncAction', paramsApi)
                    } catch (error) {
                        logError(error)
                    }
                    return context.dispatch('calcScore', params.check).then(function () {
                        return { status: false }
                    })
                }

                context.commit('setReset', { check: params.check, answer: params.answer })
                return context.dispatch('calcScore', params.check).then(function () {
                    if (response.msg && response.msg === 'Error: Api Error') {
                        return { status: false, msg: 'Error: Api Error' }
                    }
                    return { status: false }
                })
            }

            let value = response.data.value
            if (state.config.item.itemsImage.includes(params.answer_type)) {
                value = splitCommasBase64(value)
                context.commit('setAnswer', {
                    check: params.check,
                    answer: params.answer,
                    emp: params.emp,
                    date: params.date,
                    value: value,
                    bReadingFromSensor: params.bReadingFromSensor
                })
            }
            return response
        } else {
            if (canWorkOffline) {
                paramsApi.action = 'answer'
                return context
                    .dispatch('syncAction', paramsApi)
                    .then(() => {
                        return context.dispatch('calcScore', params.check).then(function () {
                            return { status: false }
                        })
                    })
                    .catch(() => {
                        return context.dispatch('calcScore', params.check).then(function () {
                            return { status: false }
                        })
                    })
            } else {
                context.commit('setReset', { check: params.check, answer: params.answer })
                return context.dispatch('calcScore', params.check).then(function () {
                    return { status: false }
                })
            }
        }
    },
    async apply(context, params) {
        let subtask = context.getters.getSubtasks(params.check, params.answer)
        let item = state.records[params.check].items[params.answer]
        let items = context.getters.getItemsBySection(params.check, params.answer)
        let userRol = params.userRol ? params.userRol : false
        let filters = false

        if (userRol) {
            filters = Object.fromEntries(
                Object.entries(items).filter(([key, it]) => {
                    if (!it.config) return true
                    if (it.config.roles && it.config.roles.includes(userRol)) return true
                    return false
                })
            )
            items = filters
        }

        context.commit('setApply', {
            check: params.check,
            answer: params.answer,
            emp: params.emp,
            date: params.date == 0 ? params.date : false,
            apply: params.apply
        })

        if (item.type == FORMAT.TITLE) {
            Object.keys(items).map(function (item_id, i) {
                var it = items[item_id]
                if (!it.mandatory) {
                    context.commit('setApply', {
                        check: params.check,
                        answer: it.item_id,
                        date: params.apply == 0 ? params.date : false,
                        apply: params.apply
                    })
                }
            })
        }

        await context.dispatch('calcScore', params.check)

        if (subtask.length > 0) {
            Object.keys(subtask).map(function (item_id, i) {
                var item = subtask[item_id]
                context.commit('setReset', {
                    check: params.check,
                    answer: item
                })
            })
        }

        const checklist = context.getters.getById(params.check)
        const total = checklist.total_score
        const answered = checklist.completed
        const score = checklist.score
        const na = Object.values(checklist.items).filter((item) => item.apply === '0').length
        const resume = { it: 0, is: 0, a: 0, n: 0, na: na }

        for (let sectionId in checklist.sections) {
            const section = checklist.sections[sectionId]

            for (let itemId in section.items) {
                let item = section.items[itemId]
                if (!shouldHideItem(item) && item.is_visible) {
                    if (!item.complete_date && item.apply) resume.it++ // if item does not have completed_date
                    if (item.message) resume.n++ // if item has notes
                    if (item.alerts && item.alerts.issues.length > 0) resume.is++ // if item has alerts and has issue
                    if (item.alerts && item.alerts.issues.length == 0) resume.a++ // if item has alerts and does not have issue
                    if (item.apply === 0) resume.na++
                }
            }
        }

        for (let id in checklist.items) {
            const item = checklist.items[id]

            if (!shouldHideItem(item) && item.is_visible && !state.config.item.itemsExcluded.includes(item.type)) {
                if (!item.complete_date && item.apply && !item.planned_to_date) resume.it++ // if item does not have completed_date
                if (item.message) resume.n++ // if item has notes
                if (item.alerts && item.alerts.issues.length > 0) resume.is++ // if item has alerts and has issue
                if (item.alerts && item.alerts.issues.length == 0) resume.a++ // if item has alerts and does not have issue
                if (!item.apply) resume.na++ // if item has not notes
            }
        }

        let paramsApi = {
            checklist: params.check,
            task: params.answer,
            employee: params.emp,
            complete_date: params.date,
            apply: params.apply,
            total: total,
            answered: answered,
            score: score,
            resume: JSON.stringify(resume)
        }

        if (fnCheckConnection() && context.rootGetters.getFastConnection) {
            return api
                .post('checklist/task/apply', paramsApi)
                .then(function (response) {
                    if (!response.status && context.rootGetters.getOffline) {
                        paramsApi.action = 'apply'
                        return context.dispatch('syncAction', paramsApi)
                    } else {
                        context.commit('setReset', { check: params.check, answer: params.answer, getters: context.getters })
                    }
                })
                .catch(() => {
                    if (context.rootGetters.getOffline) {
                        paramsApi.action = 'apply'
                        return context.dispatch('syncAction', paramsApi)
                    } else {
                        context.commit('setReset', { check: params.check, answer: params.answer, getters: context.getters })
                    }
                })
        } else {
            if (context.rootGetters.getOffline) {
                paramsApi.action = 'apply'
                return context.dispatch('syncAction', paramsApi)
            } else {
                context.commit('setReset', { check: params.check, answer: params.answer, getters: context.getters })
            }
        }
    },
    async applyrecurrent(context, params) {
        let subtask = context.getters.getSubtasks(params.check, params.answer)

        // apply
        context.commit('setApply', {
            check: params.check,
            answer: params.answer,
            emp: params.emp,
            date: params.apply == 0 ? params.date : false,
            apply: params.apply
        })

        var item = state.records[params.check].items[params.answer]
        if (item.type == FORMAT.TITLE) {
            let items = context.getters.getItemsBySection(params.check, params.answer)

            Object.keys(items).map(function (item_id, i) {
                var it = items[item_id]
                if (!it.mandatory) {
                    context.commit('setApply', {
                        check: params.check,
                        answer: it.item_id,
                        date: params.apply == 0 ? params.date : false,
                        apply: params.apply
                    })
                }
            })
        }

        // calculate score
        await context.dispatch('calcScore', params.check)

        // reset child items
        if (subtask.length > 0) {
            Object.keys(subtask).map(function (item_id, i) {
                var item = subtask[item_id]
                context.commit('setReset', { check: params.check, answer: item })
            })
        }

        // calculate score

        const checklist = context.getters.getById(params.check)
        const total = checklist.total
        const answered = checklist.completed
        const score = ((parseFloat(answered) * 100) / parseFloat(total)).toFixed(2)
        const na = Object.values(checklist.items).filter((item) => item.apply === '0').length
        const resume = { it: 0, is: 0, a: 0, n: 0, na: na }

        for (let id in checklist.items) {
            const item = checklist.items[id]

            if (!shouldHideItem(item) && item.is_visible && !state.config.item.itemsExcluded.includes(item.type)) {
                if (!item.complete_date && item.apply && !item.planned_to_date) resume.it++ // if item does not have completed_date
                if (item.message) resume.n++ // if item has notes
                if (item.alerts && item.alerts.issues.length > 0) resume.is++ // if item has alerts and has issue
                if (item.alerts && item.alerts.issues.length == 0) resume.a++ // if item has alerts and does not have issue
                if (!item.apply) resume.na++
            }
        }

        return api
            .post('checklist/task/applyrecurrent', {
                checklist: params.check,
                task: params.answer,
                employee: params.emp,
                complete_date: params.date,
                apply: params.apply,
                total: total,
                answered: answered,
                score: score,
                resume: JSON.stringify(resume)
            })
            .then(function (response) {
                if (!response.status) {
                    context.commit('setReset', { check: params.check, answer: params.answer })
                }
            })
    },
    async reset(context, params) {
        const canWorkOffline = context.rootGetters.getOffline
        if (context.rootGetters.getOffline || fnCheckConnection()) {
            let subtask = context.getters.getSubtasks(params.check, params.answer)

            context.commit('setReset', {
                check: params.check,
                answer: params.answer,
                emp: params.emp,
                date: params.date
            })
            await context.dispatch('calcScore', params.check)

            if (subtask.length > 0) {
                Object.keys(subtask).map(function (item_id, i) {
                    var item = subtask[item_id]

                    context.commit('setReset', {
                        check: params.check,
                        answer: item
                    })
                })
            }

            const checklist = context.getters.getById(params.check)
            const total = checklist.total
            const answered = checklist.completed
            const score = ((parseFloat(answered) * 100) / parseFloat(total)).toFixed(2)
            const na = Object.values(checklist.items).filter((item) => item.apply === '0').length
            const resume = { it: 0, is: 0, a: 0, n: 0, na: na }

            let paramsApi = {
                checklist: params.check,
                task: params.answer,
                total: total,
                answered: answered,
                resume: JSON.stringify(resume),
                score: score
            }

            if (canWorkOffline) {
                await context.dispatch('clearOfflineQueue', { data: paramsApi, action: 'reset' })
            }

            if (fnCheckConnection() && context.rootGetters.getFastConnection) {
                const { response } = await postTaskWithRetry(api, 'checklist/task/reset', paramsApi)

                if (!response.status) {
                    paramsApi.action = 'reset'
                    try {
                        await context.dispatch('syncAction', paramsApi)
                    } catch (error) {
                        console.error('Error in reset():', error)
                    }
                }

                return { status: true }
            } else {
                paramsApi.action = 'reset'
                return context
                    .dispatch('syncAction', paramsApi)
                    .then(function () {
                        return { status: true }
                    })
                    .catch(() => {
                        return { status: true }
                    })
            }
        } else {
            return { status: false }
        }
    },
    async add(context, params) {
        var type = context.state.type
        var oData = { template: params.tpl_id }
        if (params.emp_ref) {
            oData.employee_ref = params.emp_ref
        }
        if (params.checklist_ref) {
            oData.checklist_ref = params.checklist_ref
        }
        if (params.item_ref) {
            oData.item_ref = params.item_ref
        }

        const currentRecords = { ...context.state.records }

        const canWorkOffline = context.rootGetters.getOffline

        if (fnCheckConnection() && context.rootGetters.getFastConnection) {
            return api.post('checklist/add', oData).then((response) => {
                context.dispatch('format', { data: response.data })
                context.dispatch('calcScore', Object.keys(response.data)[0])

                // para que se vea el empty state de la tab completados apenas se agrega el primer registro
                context.commit('setRecordsAreLoaded', true)

                const newRecord = response.data[Object.keys(response.data)[0]]
                const newRecords = { ...currentRecords, [newRecord.id]: newRecord }
                context.commit('setRecords', { records: newRecords })
                context.dispatch('adjustDraftRecordsCounters', {
                    templateId: params.tpl_id,
                    delta: 1
                })
                return newRecord
            })
        } else {
            if (canWorkOffline) {
                const items = JSON.parse(JSON.stringify(context.state.templates[params.tpl_id].items))
                const itemsWithTempIds = items.map((item) => {
                    if (parseInt(item.type) === FORMAT.MULTIPLE && item.config && item.config.values) {
                        item.config.values = item.config.values.map((option) => {
                            if (option.c) {
                                return { v: option.v, c: option.c }
                            } else if (option.v && !option.c) {
                                return { v: option.v, c: false }
                            } else {
                                return { v: option, c: false }
                            }
                        })
                    }
                    return {
                        ...item,
                        alerts: item.alerts ? JSON.parse(JSON.stringify(item.alerts)) : false,
                        type: parseInt(item.type),
                        item_id: item.id,
                        complete_date: false,
                        value: null,
                        review: false,

                        alerts_triggers: item.alerts ? JSON.parse(JSON.stringify(item.alerts)) : false
                    }
                })

                const oTemplateItems = {}
                itemsWithTempIds.forEach((item) => {
                    oTemplateItems[item.id] = item
                })
                const user = store.getters['loginUser/getUserInformation']

                const newRegister = {
                    business_date: moment(),
                    category_id: context.state.templates[params.tpl_id].category_id,
                    complete_date: false,
                    completed: false,
                    config: context.state.templates[params.tpl_id].config,
                    created_date: moment(),
                    data: false,
                    def: state.config,
                    employee: { ...user },
                    employee_ref: { ...user },
                    employees: [],
                    id: 'tmpitw' + Math.floor(Date.now() / 1000),
                    issue_id: false,
                    items: JSON.parse(JSON.stringify(oTemplateItems)),
                    justify_id: false,
                    recover: false,
                    score: 0,
                    status: 0,
                    template_id: params.tpl_id,
                    title: context.state.templates[params.tpl_id].name,
                    total: items.length,
                    type: context.state.templates[params.tpl_id].type,
                    unanswered: items.length,
                    uncompleted: 0,
                    requireSync: true
                }
                const syncData = {
                    action: 'add',
                    checklist: newRegister.id,
                    employee: newRegister.employee.id,
                    template_ref: params.tpl_id,
                    type: newRegister.type
                }

                store.dispatch('register/syncAction', syncData)
                const allItems = { [newRegister.id]: newRegister, ...context.state.records }

                await context.dispatch('format', { data: allItems })
                await context.dispatch('calcScore', newRegister.id)
                context.dispatch('adjustDraftRecordsCounters', {
                    templateId: params.tpl_id,
                    delta: 1
                })

                await db_registers
                    .setItem(newRegister.id, JSON.parse(JSON.stringify(newRegister)))
                    .then(() => {
                        log('Register added to localForage')
                    })
                    .catch((err) => {
                        logError('Error adding register to localForage', err)
                    })

                return state.records[newRegister.id]
            }
        }
    },
    update(context, params) {
        const checklist = context.getters.getById(params.checklist)
        const wasDraft = checklist && parseInt(checklist.status, 10) === 0 && !checklist.complete_date
        const willBeDraft = parseInt(params.status, 10) === 0
        var oData = {
            checklist: params.checklist,
            status: params.status
        }
        return api
            .post('checklist/update', oData)
            .then((response) => {
                if (response.status) {
                    state.records[oData.checklist].status = oData.status
                    state.records[oData.checklist].complete_date = null

                    if (checklist && !wasDraft && willBeDraft) {
                        context.dispatch('adjustDraftRecordsCounters', {
                            templateId: checklist.template_id,
                            delta: 1
                        })
                    }
                }

                return response
            })
            .catch((error) => {
                // Handle any errors here
                console.error('Register reope failed:', error)
                throw error
            })
    },
    delete(context, params) {
        const checklist = context.getters.getById(params.check)
        const wasDraft = checklist && parseInt(checklist.status, 10) === 0
        var oData = {
            checklist: params.check,
            employee: params.emp,
            status: -1
        }

        if (params.ref_item) {
            oData['ref_item'] = params.ref_item
        }

        return api.post('checklist/update', oData).then(function (response) {
            if (response.status && wasDraft) {
                context.dispatch('adjustDraftRecordsCounters', {
                    templateId: checklist.template_id,
                    delta: -1
                })
            }

            db_registers.removeItem(params.check).then(() => {
                // log('Register removed from localForage')
            })
            return response
        })
    },
    loadStats(context, params) {
        context.commit('resetStats', {})
        var currentDate = moment(params.date)

        // SET ALERTS --------------------------------------------------------------

        // SET ACTIVITY ------------------------------------------------------------

        // -- Created Registers
        context
            .dispatch('loadFromDate', {
                date: currentDate.format('YYYY-MM-DD')
            })
            .then(function (response) {
                let result = response.data ? Object.keys(response.data).length : 0
                if (response.data && result > 0) {
                    context.commit('setStats', {
                        where: 'activity',
                        key_name: 'created',
                        values: {
                            name: i18n.tc('home.activity.registers_generated', result),
                            value: result
                        }
                    })
                }
            })
    },
    format({ state, context, commit, dispatch, rootState }, params = {}) {
        // var append = typeof params.append != "undefined" ? params.append : false;
        const aItems = {}

        const _itemCustomMap = {}
        Object.keys(state.templates || {}).forEach(function (tplId) {
            _itemCustomMap[tplId] = buildItemCustomMapFromTemplates({ [tplId]: state.templates[tplId] })
        })

        if (typeof params.data !== 'undefined') {
            Object.keys(params.data).map(function (item_id, i) {
                var list = params.data[item_id]

                // Offline cases
                if (list.created_date && typeof list.created_date === 'string') {
                    list.created_date = moment(list.created_date)
                }

                if (list.business_date && typeof list.business_date === 'string') {
                    list.business_date = moment(list.business_date)
                }

                if (list.complete_date && typeof list.complete_date === 'string') {
                    list.complete_date = moment(list.complete_date)
                }

                // Timezone set
                list.business_date = list.business_date ? (moment.isMoment(list.business_date) ? list.business_date : moment(list.business_date * 1000)) : false
                list.created_date = list.created_date ? (moment.isMoment(list.created_date) ? list.created_date : moment(list.created_date * 1000)) : false
                list.complete_date = list.complete_date ? (moment.isMoment(list.complete_date) ? list.complete_date : moment(list.complete_date * 1000)) : false

                list.def = state.config

                // Loop for all items
                const _tplItemMap = _itemCustomMap[list.template_id] || {}
                const tpl = state.templates[list.template_id]

                Object.keys(list.items).map(function (item_id, i) {
                    var item = list.items[item_id]

                    if ((!item.custom || item.custom.length === 0) && _tplItemMap[item_id]) {
                        item.custom = JSON.parse(JSON.stringify(_tplItemMap[item_id]))
                    } else if (_tplItemMap[item_id]) {
                        mergeTemplateCustomIntoItem(item, _tplItemMap[item_id])
                    }

                    // Apply del custom según fecha del registro (items en tpl.items y tpl.sections)
                    const templateItem = getTemplateItemById(tpl, item_id)
                    if (templateItem && Array.isArray(templateItem.custom) && templateItem.custom.length > 0) {
                        const dateAwareApply = getCustomApplyForRegister(templateItem, list.created_date)
                        item.apply = String(dateAwareApply)
                        // Sincronizar el primer custom con el apply efectivo para que getCustomApply(item) y shouldHideItem(item) sean coherentes con múltiples custom
                        if (item.custom && item.custom.length > 0) {
                            item.custom[0].apply = dateAwareApply
                        }
                    }

                    // Timezone set
                    item.complete_date = item.complete_date ? (moment.isMoment(item.complete_date) ? item.complete_date : moment(item.complete_date * 1000)) : false
                    if (item.review) {
                        item.review['complete_date'] = item.review.complete_date ? (moment.isMoment(item.review.complete_date) ? item.review.complete_date : moment(item.review.complete_date * 1000)) : false
                    }

                    // If type image - Split value to array
                    if (state.config.item.itemsImage.includes(item.type)) {
                        item.value = item.value && typeof item.value === 'string' ? splitCommasBase64(item.value) : item.value
                    }

                    item.complete_date = item.complete_date
                    item.available = false

                    list.items[item_id] = { ...item }
                    Vue.set(list.items, item_id, { ...item })
                })

                aItems[list.id] = list
            })
        }

        commit('setRecords', { records: aItems })
    },
    calcScore({ state, context, commit, dispatch, rootState }, id) {
        const checklists = state.records

        Object.keys(checklists).map((recordId) => {
            const checklist = checklists[recordId]

            if (typeof id === 'undefined' || recordId === id) {
                // commented out because it's not used
                // var past_score = checklist.score ? checklist.score : 0

                checklist.def = state.config
                checklist.uncompleted = 0
                checklist.completed = 0
                checklist.unanswered = 0
                checklist.total = 0
                checklist.score = 0
                checklist.justify_id = checklist.justify_id ? checklist.justify_id : checklist.justify_msg ? '0' : false

                checklist.items = buildHierarchicalOrderedItems(checklist.items)
                const orderedItems = Object.values(checklist.items)

                // Loop for all items
                orderedItems.forEach((item) => {
                    item.is_visible = true
                    item.alerts = false
                    // Subsubitem: item whose parent is itself a subitem (parent has parent_id)
                    const parent = item.parent_id ? checklist.items[item.parent_id] : null
                    item.is_subsubitem = !!(parent && parent.parent_id)

                    item.order = getBaseOrder(item)

                    // SUBITEMS
                    if (typeof item.conditions !== 'undefined' && item.conditions != null && item.conditions) {
                        const subitemConditionsData = item.conditions
                        const conditionsCombinator = typeof subitemConditionsData.operator !== 'undefined' ? subitemConditionsData.operator : 'AND'

                        const conditions = subitemConditionsData.values

                        let shouldDisplay = null
                        let parentItemOrder = item.parent_id && checklist.items[item.parent_id] ? parseFloat(checklist.items[item.parent_id].order) : -1

                        // condition key === subitem parent_id and parent_id === parent item item_id
                        for (const key in conditions) {
                            let show = false
                            item.is_visible = false

                            const condition = conditions[key]
                            const conditionOperator = condition && typeof condition.operator !== 'undefined' ? condition.operator : '='
                            const conditionValue = condition && typeof condition.value !== 'undefined' ? condition.value : false

                            const parentConditionItem = checklist.items[key]
                            const parentItem = parentConditionItem || null
                            const parentItemValue = parentItem && typeof parentItem.value !== 'undefined' ? parentItem.value : null

                            if (parentConditionItem && (parentItemValue !== null || [FORMAT.CHECK, FORMAT.DOCUMENT].includes(parentConditionItem.type)) && parentConditionItem.is_visible) {
                                if (conditionOperator == 'true' && conditionValue == 'true') {
                                    show = true
                                } else {
                                    // Get task type from parent
                                    switch (parentConditionItem.type) {
                                        //   switch (item.type) {
                                        case FORMAT.CHECK:
                                            show = compare(conditionValue, parentItemValue, conditionOperator)
                                            break
                                        case FORMAT.EMAIL:
                                        case FORMAT.DOCUMENT:
                                        case FORMAT.YESNO:
                                            show = compare(parseInt(conditionValue) == 1 || (conditionValue == '' && conditionOperator == '!empty'), parentItemValue == true || parentItemValue == 'true' || (parentItemValue != null && conditionValue == '' && conditionOperator == '!empty'), conditionOperator)
                                            break
                                        case FORMAT.YESNONC:
                                            show = compare(parseInt(conditionValue) || (conditionValue == '' && conditionOperator == '!empty'), parentItemValue || (conditionValue == '' && conditionOperator == '!empty'), conditionOperator)
                                            break

                                        case FORMAT.MULTIPLE:
                                            const parentItemIsMultiselect = parentItem && parentItem.config && parentItem.config.hasOwnProperty('multiselect') && parentItem.config.multiselect === 1

                                            if (!parentItemIsMultiselect) {
                                                // SINGLE SELECT
                                                show = compare(conditionValue || (conditionValue === '' && conditionOperator === '!empty'), parentItemValue, conditionOperator)
                                            } else {
                                                // MULTIPLE SELECT
                                                if (Array.isArray(parentItemValue) || (typeof parentItemValue === 'string' && parentItemValue.includes('|'))) {
                                                    const parentItemValues = Array.isArray(parentItemValue) ? parentItemValue : parentItemValue.split('|')
                                                    show = parentItemValues.some((value) => compare(conditionValue, value, conditionOperator))
                                                } else {
                                                    show = compare(conditionValue, parentItemValue, conditionOperator)
                                                }
                                            }
                                            break
                                        case FORMAT.TEXT:
                                        case FORMAT.TEXTAREA:
                                        case FORMAT.PHOTO:
                                        case FORMAT.DATE:
                                        case FORMAT.TIME:
                                        case FORMAT.DATETIME:
                                        case FORMAT.SIGNATURE:
                                            show = compare(conditionValue, parentItemValue, conditionOperator)
                                            break
                                        case FORMAT.NUMBER:
                                            var aux = parentItemValue.toString().replace(',', '.')
                                            show = compare(parseFloat(aux), conditionValue, conditionOperator) || (conditionValue == '' && conditionOperator == '!empty')
                                            break
                                        case FORMAT.TEMPERATURE:
                                            var aux = parentItemValue.toString().replace(',', '.')
                                            show = compare(parseFloat(aux), conditionValue, conditionOperator) || (conditionValue == '' && conditionOperator == '!empty')
                                            break
                                        default:
                                            break
                                    }
                                }
                            }
                            if (shouldDisplay == null) shouldDisplay = show
                            else {
                                switch (conditionsCombinator) {
                                    case 'AND':
                                        shouldDisplay = shouldDisplay && show
                                        break
                                    case 'OR':
                                        shouldDisplay = shouldDisplay || show
                                        break
                                }
                            }
                            // if (show) {
                            //     itemOrder = checklist.items[key].order
                            // }

                            if (parentItemOrder === -1 && parentConditionItem) {
                                parentItemOrder = parseFloat(parentConditionItem.order)
                            }
                        }

                        item.is_visible = shouldDisplay
                        // item.order = parseFloat(parentItemOrder) + 0.5

                        const subitemOrder = getBaseOrder(item)
                        if (!Number.isNaN(parentItemOrder) && parentItemOrder !== -1 && !Number.isNaN(subitemOrder)) {
                            item.order = parentItemOrder + subitemOrder / 10000
                        } else {
                            item.order = subitemOrder
                        }
                    }

                    // Alerts TODO
                    if (item.alerts_triggers) {
                        const alerts = item.alerts_triggers
                        const itemValue = item.value

                        let oAlert = {}
                        let message = ''
                        let preview = ''
                        let issues = []
                        let classes = []

                        // SHOW ALERTS
                        for (const i in alerts) {
                            const alert = alerts[i]
                            const operator = alert.operator ? alert.operator : '='
                            const alertValue = alert.value

                            let show = false

                            if ((itemValue !== null || [FORMAT.CHECK, FORMAT.DOCUMENT].includes(item.type)) && item.is_visible) {
                                if (operator == 'true' && alertValue == 'true') {
                                    show = true
                                } else {
                                    switch (item.type) {
                                        case FORMAT.CHECK:
                                            show = compare(alertValue, itemValue, operator)
                                            break
                                        case FORMAT.EMAIL:
                                        case FORMAT.DOCUMENT:
                                        case FORMAT.YESNO:
                                            show = compare(parseInt(alertValue) == 1 || (alertValue == '' && operator == '!empty'), itemValue == true || itemValue == 'true', operator)
                                            break
                                        case FORMAT.YESNONC:
                                            show = compare(parseInt(alertValue) || (alertValue == '' && operator == '!empty'), itemValue, operator)
                                            break
                                        case FORMAT.MULTIPLE:
                                            const itemIsMultiselect = item && item.config && item.config.hasOwnProperty('multiselect') && item.config.multiselect === 1

                                            // MULTIPLE SELECT
                                            if (itemIsMultiselect && (Array.isArray(itemValue) || (typeof itemValue === 'string' && itemValue.includes('|')))) {
                                                const itemValues = Array.isArray(itemValue) ? itemValue : itemValue.split('|')
                                                show = itemValues.some((value) => compare(alertValue || (alertValue === '' && operator === '!empty'), value, operator))
                                            } else {
                                                // SINGLE SELECT
                                                show = compare(alertValue || (alertValue === '' && operator === '!empty'), itemValue, operator)
                                            }
                                            break
                                        case FORMAT.TEXT:
                                        case FORMAT.TEXTAREA:
                                        case FORMAT.PHOTO:
                                        case FORMAT.DATE:
                                        case FORMAT.TIME:
                                        case FORMAT.DATETIME:
                                        case FORMAT.SIGNATURE:
                                            show = compare(alertValue, itemValue, operator)
                                            break
                                        case FORMAT.NUMBER:
                                            var aux = itemValue.toString().replace(',', '.')
                                            show = compare(parseFloat(aux), alertValue, operator) || (itemValue != '-' && operator == '!empty')
                                            break
                                        case FORMAT.TEMPERATURE:
                                            var aux = itemValue.toString().replace(',', '.')
                                            show = compare(parseFloat(aux), alertValue, operator) || (itemValue != '-' && operator == '!empty')
                                            break
                                        default:
                                            break
                                    }
                                }
                            }

                            if (show && alert.msg) {
                                if (message == '') message = alert.msg
                                else message = message + '<br/>' + alert.msg

                                if (preview == '') preview = alert.msg
                                else preview = preview + ', ' + alert.msg
                            }

                            if (show && alert.tpl) {
                                issues.push(alert.tpl)
                            }
                            if (show && alert.class) {
                                if (alert.class == 'alert') {
                                    alert.class = 'warning'
                                }
                                classes.push(alert.class)
                            }
                        }

                        oAlert = {
                            preview: preview,
                            message: message,
                            issues: issues,
                            class: classes
                        }

                        if (oAlert.message.length > 0 || oAlert.issues.length > 0) {
                            item.alerts = oAlert
                            Vue.set(item, 'alerts', oAlert)
                        }
                    }

                    // Scoring: excluir cuando item.apply es falsy O custom.apply es 0 (lógica AND original)
                    if (((!item.parent_id && checklist.def.item.itemsExcluded.indexOf(item.type) == -1) || (item.parent_id && item.is_visible)) && !item.planned_to_date && Number(item.apply) > 0 && getCustomApply(item) !== 0) {
                        switch (item.type) {
                            case FORMAT.YESNO: // SI/NO
                            case FORMAT.YESNONC: // SI/NO/NSNC
                            case FORMAT.CHECK: // CHECKS
                            case FORMAT.TEXT: // TEXTO
                            case FORMAT.PHOTO: // FOTO
                            case FORMAT.NUMBER: // NUMBER
                            case FORMAT.DATE: // DATE
                            case FORMAT.TEXTAREA: // TEXTAREA
                            case FORMAT.MULTIPLE: // MULTIPLE SELECT
                            case FORMAT.TIME: // TIME
                            case FORMAT.DATETIME: // DATETIME
                            case FORMAT.SIGNATURE: // SIGNATURE
                            case FORMAT.TEMPERATURE: // TEMPERATURE
                            case FORMAT.EMAIL: // EMAIL
                            case FORMAT.DOCUMENT: // DOCUMENT
                                if (item.complete_date == false && item.apply) checklist.unanswered++
                                else {
                                    // if item is required to be reviewed
                                    if (item.require_review) {
                                        checklist.completed += item.review ? 1 : 0.5
                                    } else checklist.completed += 1 // if item is not required to be reviewed
                                }
                                break
                            default:
                                break
                        }
                        if (![FORMAT.LINK, FORMAT.RESOURCE].includes(item.type)) {
                            checklist.total++
                        }
                    }
                })

                checklist.score = Math.round((parseFloat(checklist.completed) * 100) / parseInt(checklist.total))

                // ORDER
                Object.keys(checklists).map(function (item_id, i) {
                    let list = checklists[item_id]
                    list.items = buildHierarchicalOrderedItems(list.items)
                    checklists[item_id] = { ...list }
                })

                checklists[checklist.id] = { ...checklist }
            }
        })

        commit('setScore', { records: checklists })
    },
    // load records list from a single record template
    loadRecords(context, params) {
        if (!params.noRefresh && fnCheckConnection() && context.rootGetters.getFastConnection) {
            if (!fnCheckConnection()) {
                return loadItemsOffline(context.commit, context.dispatch, params.template)
            }

            context.commit('setIsLoading', true)
            context.commit('setRecordsAreLoaded', false)

            const baseAPIParams = {
                number_pages: 1,
                number_items: 20
            }

            // Primera llamada con state: 0 (drafts)
            const draftAPIParams = { ...baseAPIParams, state: 0 }
            const completedAPIParams = { ...baseAPIParams, state: 1 }

            return Promise.all([api.post('checklist/resume/' + params.template, draftAPIParams), api.post('checklist/resume/' + params.template, completedAPIParams)])
                .then(([draftResponse, completedResponse]) => {
                    // Verificar que ambas respuestas sean válidas
                    if (!draftResponse.status || !completedResponse.status) {
                        console.error('API returned error status:', { draft: draftResponse, completed: completedResponse })
                        return loadItemsOffline(context.commit, context.dispatch, params.template)
                    }

                    const draftData = draftResponse.data
                    const completedData = completedResponse.data

                    if (!draftData || !draftData.values || !completedData || !completedData.values) {
                        console.error('Invalid response structure:', { draft: draftData, completed: completedData })
                        context.commit('setIsLoading', false)
                        return loadItemsOffline(context.commit, context.dispatch, params.template)
                    }

                    // Guardar totalDraftRecords y totalCompletedRecords y de la primera llamada (state: 0)
                    context.commit('setTotalDraftRecords', draftData.number_items)
                    context.commit('setTotalCompletedRecords', completedData.number_items)

                    // Combinar los values de ambas respuestas
                    const combinedValues = { ...draftData.values, ...completedData.values }

                    const draftSchema = draftData.schema && !Array.isArray(draftData.schema) && Object.values(draftData.schema).length > 0 ? draftData.schema : null
                    const completedSchema = completedData.schema && !Array.isArray(completedData.schema) && Object.values(completedData.schema).length > 0 ? completedData.schema : null

                    const combinedSchema = draftSchema || completedSchema

                    // Combinar number_items
                    const combinedNumberItems = (draftData.number_items || 0) + (completedData.number_items || 0)

                    // for offline storage - guardar todos los registros
                    const allValues = Object.values(combinedValues)
                    _.forEach(allValues, async function (value, key) {
                        await db_registers.setItem(value.id, value)
                        if (!(await db_registers_schemas.getItem(params.template))) {
                            await db_registers_schemas.setItem(params.template, combinedSchema)
                        }
                    })

                    // Crear objeto combinado para mergeOffline
                    const combinedResponseData = {
                        values: combinedValues,
                        schema: combinedSchema,
                        number_items: combinedNumberItems
                    }

                    context.dispatch('mergeOffline', { data: combinedResponseData }).then(function (offlineMergedResponse) {
                        context.dispatch('format', { data: offlineMergedResponse.data.values })
                        context.commit('setSchema', { data: offlineMergedResponse.data.schema })
                        context.dispatch('calcScore')

                        context.commit('setRecordsAreLoaded', true)
                        context.commit('setIsLoading', false)
                    })
                })
                .catch((error) => {
                    console.error('Error in register.js loadRecords. Error:', error)
                    context.commit('setIsLoading', false)
                    return loadItemsOffline(context.commit, context.dispatch, params.template)
                })
        } else if (!params.noRefresh) {
            loadItemsOffline(context.commit, context.dispatch, params.template)
            context.dispatch('calcScore')
        }
    },
    // FOR PAGINATION BY TAB
    loadPageRecordsByTab(context, params) {
        context.commit('setIsLoading', true)
        context.commit('setRecordsAreLoaded', false)

        if (!fnCheckConnection()) {
            return loadItemsOffline(context.commit, context.dispatch)
        }

        const apiParams = {
            number_pages: params.number_pages,
            number_items: params.number_items,
            state: params.activeTab === 'draft' ? 0 : 1
        }

        return api
            .post('checklist/resume/' + params.id, apiParams)
            .then((response) => {
                // Validate response status and data structure
                if (!response.status) {
                    console.error('API returned error status:', response)
                    context.commit('setIsLoading', false)
                    return loadItemsOffline(context.commit, context.dispatch)
                }

                if (!response.data || !response.data.values) {
                    console.error('Invalid response structure:', response)
                    context.commit('setIsLoading', false)
                    return loadItemsOffline(context.commit, context.dispatch)
                }

                // _.forEach(_.values(responseRecords), async function (value, key) {
                //     await db_registers.setItem(value.id, value)
                //     await db_registers_schemas.setItem(value.id, response.data.schema)
                // })

                // Guardar totalDraftRecords y totalCompletedRecords de la respuesta de la API

                const responseRecords = response.data.values
                const responseRecordsStatus = apiParams.state

                // Limpiar registros del mismo status de la base de datos local antes de guardar los nuevos
                db_registers
                    .iterate(function (value, key, iterationNumber) {
                        if (value.status === responseRecordsStatus) {
                            return db_registers.removeItem(key)
                        }
                    })
                    .then(function () {
                        // Guardar los nuevos registros después de limpiar
                        const savePromises = Object.values(responseRecords).map(async function (value) {
                            await db_registers.setItem(value.id, value)
                            await db_registers_schemas.setItem(value.id, response.data.schema)
                        })

                        return Promise.all(savePromises)
                    })
                    .then(function () {
                        // Continuar con el flujo normal después de guardar en BD local
                        return context.dispatch('mergeOffline', { data: response.data })
                    })
                    .then(function (response2) {
                        const oppositeStatus = responseRecordsStatus === 0 ? 1 : 0

                        // Obtener del state actual de la store los registros con status opuesto al obtenido en la respuesta de la API
                        const currentRecordsWithOppositeStatus = {}
                        Object.keys(state.records).forEach((recordId) => {
                            const record = state.records[recordId]
                            if (record.status === oppositeStatus) {
                                currentRecordsWithOppositeStatus[recordId] = record
                            }
                        })

                        const combinedRecords = { ...responseRecords, ...currentRecordsWithOppositeStatus }

                        context.dispatch('format', { data: combinedRecords })
                        context.commit('setSchema', { data: response.data.schema })
                        // context.commit('setTotalRecords', response.data.number_items)
                        context.dispatch('calcScore')
                        context.commit('setRecordsAreLoaded', true)
                        context.commit('setIsLoading', false)

                        if (apiParams.state === 0) {
                            context.commit('setTotalDraftRecords', response.data.number_items)
                        } else {
                            context.commit('setTotalCompletedRecords', response.data.number_items)
                        }
                    })
            })
            .catch((error) => {
                console.error('Error in register.js loadPageRecordsByTab. Error:', error)
                context.commit('setIsLoading', false)
                return loadItemsOffline(context.commit, context.dispatch)
            })
    },
    loadFromDate(context, params) {
        var cType = 3

        params.date = params.date ? params.date : moment().format('YYYY-MM-DD')
        return api.get('checklist/' + cType + '/' + params.date, {}).then(function (response) {
            return response
        })
    },
    recoverValues(context, params) {
        // TODO APPEND
        return api.post('checklist/recover/' + params.check, {}).then(function (response) {
            if (response.status) {
                context.dispatch('format', { data: response.data })
                context.dispatch('calcScore', params.id)
            } else {
                router.go()
            }

            return response.data
        })
    },
    syncAction(context, params) {
        params.queued_date = moment().valueOf()
        params.id = moment().valueOf()

        return context.dispatch('checklist/addRegisterToQueue', params, { root: true }).then(() => {
            return { status: true }
        })
    },
    sync(context, queue) {
        let checklists = {}
        for (let key in queue) {
            let queueElement = queue[key]
            if (typeof checklists[queueElement.checklist] === 'undefined') {
                checklists[queueElement.checklist] = {
                    items: [],
                    resume: { it: 0, is: 0, a: 0, n: 0, na: 0 },
                    total: 0,
                    answered: 0,
                    score: 0
                }
            }

            checklists[queueElement.checklist].resume = queueElement.resume
            checklists[queueElement.checklist].total = queueElement.total
            checklists[queueElement.checklist].answered = queueElement.answered
            checklists[queueElement.checklist].score = queueElement.score
            checklists[queueElement.checklist].items.push(queueElement)
        }
        return api
            .post(`checklist/${state.type}/sync`, { checklists: JSON.stringify(checklists) })
            .then(function (response) {
                if (!response.status) {
                    throw new Error('API error') //Por si falla algo de email
                }
                if (response.data) {
                    const issues = response.data.filter((queue) => queue.issue_id)
                    if (issues.length > 0) {
                        context.dispatch('updateIssueOfflineDatabase', issues)
                        response.data = response.data.filter((queue) => !queue.issue_id)
                    }
                }
                return context
                    .dispatch('updateOfflineDatabase', response)
                    .then(() => {
                        return response
                    })
                    .catch(function (error) {
                        logError(error)
                        return false
                    })
            })
            .catch(function (error) {
                logError(error)
                return false
            })
    },
    async updateIssueOfflineDatabase(context, response) {
        if (response) {
            const checklists = response
            checklists.forEach(async (checklist) => {
                //Actualizar el estado de sincronización de los registros
                await store.dispatch('issues/updateStateOfSync', checklist)

                // Actualizar el id de sincronización de los registros
                if (checklist && checklist.checklist_temporal && checklist.checklist) {
                    const register = await db_issues.getItem(checklist.checklist_temporal)
                    if (register) {
                        register.id = checklist.checklist
                        register.issue_id = checklist.issue_id
                        await db_issues.removeItem(checklist.checklist_temporal)
                        await db_issues.setItem(checklist.checklist, register)
                    }
                    //change the key of the item and id
                    const copy = store.getters['issues/getAll']
                    copy[checklist.checklist_temporal].id = checklist.checklist
                    copy[checklist.checklist_temporal].requireSync = false
                    copy[checklist.checklist_temporal].issue_id = checklist.issue_id
                    copy[checklist.checklist_temporal].temporal_id = checklist.checklist_temporal
                    const updateItem = { [checklist.checklist]: copy[checklist.checklist_temporal] }
                    delete copy[checklist.checklist_temporal]

                    const allItems = { ...updateItem, ...copy }

                    await store.dispatch('issues/format', { data: allItems })

                    // update allItems
                    const newIssueItem = store.getters['issues/getAll'][copy.status]
                    newIssueItem[checklist.checklist_temporal].id = checklist.checklist
                    newIssueItem[checklist.checklist_temporal].requireSync = false
                    newIssueItem[checklist.checklist_temporal].issue_id = checklist.issue_id
                    newIssueItem[checklist.checklist_temporal].temporal_id = checklist.checklist_temporal
                    const newAllItems = { ...newIssueItem }
                    store.commit('issues/setAllItems', { items: newAllItems, type: copy.status })
                }
            })
        }
    },
    async mergeOffline({ state, context, commit, dispatch, rootState }, params = {}) {
        await db_registers_queue.iterate(function (answer, checklistId, iterationNumber) {
            if (typeof params.data.values[answer.checklist] !== 'undefined' && params.data.values[answer.checklist].items[answer.task] !== 'undefined') {
                params.data.values[answer.checklist].items[answer.task].employee = answer.employee
                params.data.values[answer.checklist].items[answer.task].complete_date = answer.complete_date
                params.data.values[answer.checklist].items[answer.task].value = answer.value.toString()
                params.data.values[answer.checklist].items[answer.task].requireSync = true
            }
        })
        return params
    },
    async updateQueueTemplateId_End(context, { old_id, new_id }) {
        var aPromises = []
        await db_registers_queue.iterate(function (answer, checklistId, iterationNumber) {
            if (answer.checklist == old_id) {
                answer.checklist = new_id
                aPromises.push(
                    new Promise(async (resolve2, reject2) => {
                        await db_registers_queue.removeItem(checklistId).then(async () => {
                            await db_registers_queue.setItem(checklistId, answer).then(() => {
                                resolve2()
                            })
                        })
                    })
                )
            }
        })

        return Promise.all(aPromises).then(() => {
            // resolve()
        })
    },
    async updateQueueTemplateId(context, { old_id, new_id }) {
        return new Promise(async (resolve, reject) => {
            await context.dispatch('updateQueueTemplateId_End', { old_id: old_id, new_id: new_id })

            resolve()
        })
    },
    async updateStateOfSync(context, params) {
        // Actualizar el estado de sincronización de los registros
        if (params && params.checklist_temporal) {
            const register = await db_registers.getItem(params.checklist_temporal)
            if (register) {
                register.requireSync = false
                await db_registers.setItem(params.checklist_temporal, register)
            }
            state.records[params.checklist_temporal].requireSync = false
        }
    },
    async updateOfflineDatabase(context, response) {
        if (response.data) {
            if (response.data.type && response.data.type !== 'add') return

            const checklists = response.data
            checklists.forEach(async (checklist) => {
                //Actualizar el estado de sincronización de los registros
                await context.dispatch('updateStateOfSync', checklist)

                // Actualizar el id de sincronización de los registros
                if (checklist && checklist.checklist_temporal && checklist.checklist) {
                    const register = await db_registers.getItem(checklist.checklist_temporal)
                    if (register) {
                        register.id = checklist.checklist
                        await db_registers.removeItem(checklist.checklist_temporal)
                        await db_registers.setItem(checklist.checklist, register)
                    }
                    const check = context.state.records[checklist.checklist] ? checklist.checklist : context.state.records[checklist.checklist_temporal] ? checklist.checklist_temporal : false
                    if (check) {
                        //change the key of the item and id
                        const copy = { ...context.state.records }
                        copy[check].id = checklist.checklist
                        copy[check].temporal_id = checklist.checklist_temporal
                        const updateItem = { [checklist.checklist]: copy[check] }
                        delete copy[checklist.checklist_temporal]

                        const allItems = { ...updateItem, ...copy }
                        context.state.records = { ...copy }
                        await context.dispatch('format', { data: allItems })
                    }
                }
            })
        }
    },
    async clearOfflineQueue(context, params) {
        const data = params.data
        const action = params.action
        //['answer', 'reset', 'validate', 'unvalidate', 'complete', 'uncomplete']
        const mapActionsToRemove = {
            answer: ['answer', 'reset'],
            reset: ['answer', 'reset'],
            validate: ['validate', 'unvalidate'],
            unvalidate: ['validate', 'unvalidate'],
            complete: ['complete']
        }

        const actionsToRemove = mapActionsToRemove[action]

        await db_registers_queue.iterate(async function (answer, key, iterationNumber) {
            const isTheSameChecklist = answer.checklist === data.checklist && answer.task === data.task
            if (actionsToRemove.includes(answer.action) && isTheSameChecklist) {
                await db_registers_queue.removeItem(key)
            }
        })
    }
}

const mutations = {
    setIsLoading(state, payload) {
        state.isLoading = payload
    },
    setRadarKPIs(state, radarKPIs) {
        state.radarKPIs = radarKPIs
    },
    setRecordsAreLoaded(state, payload) {
        state.recordsAreLoaded = payload
    },
    resetState(state) {
        Object.assign(state, getDefaultState())
    },
    setTemplates(state, payload) {
        var templates = {}

        if (payload.data != undefined) {
            Object.keys(payload.data).map(function (tpl_id, i) {
                var tpl = { ...payload.data[tpl_id] }

                tpl.last_update = tpl.last_update ? (moment.isMoment(tpl.last_update) ? tpl.last_update : moment(tpl.last_update * 1000)) : false

                templates[tpl.id] = tpl
            })
        }

        Vue.set(state, 'templates', { ...templates })
    },
    setTemplatesLoaded(state, payload) {
        state.templatesLoaded = payload
    },
    adjustTemplateDraftRecordsCount(state, payload) {
        const template = state.templates[payload.templateId]

        if (!template) {
            return
        }

        const currentDraftRecords = parseInt(template.open_count, 10) || 0
        const draftRecords = Math.max(0, currentDraftRecords + payload.delta)
        Vue.set(template, 'open_count', draftRecords)
        Vue.set(state.templates, payload.templateId, { ...template })
    },
    adjustRadarDraftRecordsKPI(state, payload) {
        if (payload.templateId == null || !payload.delta) {
            return
        }

        const radarKPIs = Array.isArray(state.radarKPIs) ? state.radarKPIs : []
        let foundKPI = false
        let changed = false

        const nextRadarKPIs = radarKPIs.map((radarKPI) => {
            if (!radarKPI || radarKPI.key !== 'draft_registers') {
                return radarKPI
            }

            foundKPI = true

            const templates = Array.isArray(radarKPI.templates) ? radarKPI.templates : []
            let foundTemplate = false
            let changedTemplate = false
            const nextTemplates = []

            templates.forEach((template) => {
                if (!template || String(template.template_id) !== String(payload.templateId)) {
                    nextTemplates.push(template)
                    return
                }

                foundTemplate = true

                const currentValue = Number(template.value) || 0
                const nextValue = Math.max(0, currentValue + payload.delta)

                if (nextValue > 0) {
                    nextTemplates.push({ ...template, value: nextValue })
                }

                changedTemplate = nextValue !== currentValue
            })

            if (!foundTemplate && payload.delta > 0) {
                nextTemplates.push({ template_id: payload.templateId, value: payload.delta })
                changedTemplate = true
            }

            if (!changedTemplate) {
                return radarKPI
            }

            changed = true

            return { ...radarKPI, templates: nextTemplates }
        })

        if (!foundKPI && payload.delta > 0) {
            state.radarKPIs = [...nextRadarKPIs, { key: 'draft_registers', templates: [{ template_id: payload.templateId, value: payload.delta }] }]
            return
        }

        if (changed) {
            state.radarKPIs = nextRadarKPIs
        }
    },
    setAnswerProp(state, payload) {
        const recordId = payload.check
        const answer = payload.answer
        const prop = payload.prop
        const value = payload.value

        state.records[recordId].items[answer][prop] = value

        Vue.set(state, 'records', { ...state.records })
    },
    setComment(state, payload) {
        let recordId = payload.check
        let answer = payload.answer
        let aux = state.records[recordId].items[answer]

        aux.message = payload.message
        aux.image = payload.image ? payload.image : false
        aux.requireSync = payload.requireSync

        Vue.set(state.records[recordId].items, answer, { ...aux })
        Vue.set(state, 'records', { ...state.records })
    },
    setCategories(state, payload) {
        var categories = state.categories
        categories = payload
        Vue.set(state, 'categories', { ...categories })
    },
    setAnswer(state, payload) {
        const recordId = payload.check
        var answer = payload.answer
        var emp = payload.emp
        var value = payload.value
        var date = payload.date
        var bReadingFromSensor = payload.bReadingFromSensor

        const copyState = { ...state.records }

        copyState[recordId].items[answer].value = value
        copyState[recordId].items[answer].requireSync = payload.requireSync
        copyState[recordId].items[answer].readingFromSensor = bReadingFromSensor

        if (date) {
            var item = copyState[recordId].items[answer]
            item.complete_date = !moment.isMoment(date) && date != false ? moment(date * 1000) : item.complete_date
        }
        if (emp) copyState[recordId].items[answer].employee = emp

        Vue.set(state, 'records', { ...copyState })
        state.records = { ...copyState }
    },
    setApply(state, payload) {
        const recordId = payload.check
        const answer = payload.answer
        const emp = payload.emp
        const apply = payload.apply
        const date = payload.date

        state.records[recordId].items[answer].apply = apply

        if (date) {
            state.records[recordId].items[answer].complete_date = !moment.isMoment(date) && date != false ? moment(date * 1000) : item.complete_date
            state.records[recordId].items[answer].value = null
        } else {
            state.records[recordId].items[answer].complete_date = false
        }

        if (emp) {
            state.records[recordId].items[answer].employee = emp
        }

        Vue.set(state, 'records', { ...state.records })
    },
    setReset(state, payload) {
        const recordId = payload.check
        const answer = payload.answer
        const emp = payload.emp ? payload.emp : false
        const date = payload.date ? payload.date : false

        state.records[recordId].items[answer].complete_date = false
        state.records[recordId].items[answer].employee = false
        state.records[recordId].items[answer].review = false
        state.records[recordId].items[answer].value = null

        if (emp && date) {
            state.records[recordId].items[answer].reset = {
                e: emp,
                d: date
            }
        }

        Vue.set(state, 'records', { ...state.records })
    },
    setValidation(state, payload) {
        const recordId = payload.check
        var answer = payload.answer
        var emp = payload.emp
        var date = payload.date

        state.records[recordId].items[answer].review = {}
        if (date) {
            var item = state.records[recordId].items[answer].review
            item.complete_date = !moment.isMoment(date) && date != false ? moment(date * 1000) : item.complete_date
        }
        if (emp) {
            var employee = store.getters['employee/getEmployee'](emp)
            state.records[recordId].items[answer].review.employee = employee
        }
        state.records[recordId].items[answer].review.value = 1

        Vue.set(state, 'records', { ...state.records })
    },
    setUnvalidation(state, payload) {
        const recordId = payload.check
        var answer = payload.answer

        state.records[recordId].items[answer].review = false

        Vue.set(state, 'records', { ...state.records })
    },
    resetStats(state, payload) {
        state.stats.alerts = {}
        state.stats.activity = {}
    },
    setStats(state, payload) {
        state.stats[payload.where] = {}
        state.stats[payload.where][payload.key_name] = payload.values
    },
    setRecords(state, payload) {
        const records = payload.records
        state.records = records
        Vue.set(state, 'records', { ...records })
    },
    setScore(state, payload) {
        const records = payload.records
        state.records = records
        Vue.set(state, 'records', { ...records })
    },
    setSchema(state, payload) {
        state.headers = payload.data
        Vue.set(state, 'headers', { ...state.headers })
    },
    async setTotalRecords(state, payload) {
        state.totalRecords = payload
        Vue.set(state, 'totalRecords', state.totalRecords)
        await db_templates.setItem('registers_items', payload)
    },
    async setTotalDraftRecords(state, payload) {
        state.totalDraftRecords = payload
        Vue.set(state, 'totalDraftRecords', state.totalDraftRecords)

        await db_templates.setItem('registers_draft_items', payload)
    },
    async setTotalCompletedRecords(state, payload) {
        state.totalCompletedRecords = payload
        Vue.set(state, 'totalCompletedRecords', state.totalCompletedRecords)
        await db_templates.setItem('registers_completed_items', payload)
    },
    setPreviousEditId(state, payload) {
        state.previousEditId = payload
        Vue.set(state, 'previousEditId', state.previousEditId)
    },
    setRequireSync(state, payload) {
        let recordId = payload.check
        let answer = payload.answer
        let apiId = payload.apiId

        if (answer) {
            recordId = state.records[recordId] ? recordId : state.records[apiId] ? apiId : false

            if (recordId) {
                state.records[recordId].items[answer].requireSync = payload.requireSync
                state.records[recordId].items[answer] = { ...state.records[recordId].items[answer] }
                Vue.set(state, 'records', { ...state.records })
            }
        }
    },
    setTabPages(state, payload) {
        state.tabPages = payload
        Vue.set(state, 'tabPages', state.tabPages)
    },
    setDraftPage(state, payload) {
        state.tabPages.draft = payload
        Vue.set(state, 'draftPage', state.tabPages)
    },
    setCompletedPage(state, payload) {
        state.tabPages.completed = payload
        Vue.set(state, 'tabPages', state.tabPages)
    }
}

function loadTemplatesOffline(context) {
    let templates = []
    let count = 0

    return db_templates
        .iterate(function (value, key, iterationNumber) {
            value = typeof value === 'string' ? JSON.parse(value) : value
            if (value.type == state.type) {
                templates.push(value)
                count++
            }
        })
        .then(function () {
            if (count > 0) {
                context.commit('setTemplates', { data: templates })
            }
            // Se marca como cargado siempre, incluso sin templates locales: distingue "sin plantillas" de "cargando".
            context.commit('setTemplatesLoaded', true)
        })
}

async function loadItemsOffline(commit, dispatch, template = false) {
    let items = {}
    let count = 0
    let schema = false
    if (template) {
        schema = await db_registers_schemas.getItem(template)
    }

    return db_registers
        .iterate(function (value, key, iterationNumber) {
            items[value.id] = value
            count++
        })
        .then(function () {
            if (count > 0) {
                dispatch('mergeOffline', { data: items }).then((response) => {
                    dispatch('format', { data: response.data })
                    commit('setSchema', { data: schema })
                    dispatch('calcScore')
                })
            }
        })
}

export default {
    namespaced: true,
    state,
    getters,
    actions,
    mutations
}
